import React, { useEffect, useState } from 'react';
import { BoxIcon, SearchIcon, PlusIcon, DollarSignIcon, ClockIcon, EditIcon, TrashIcon, UserIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { StatsCard } from '../components/ui/StatsCard';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { EmptyBoxPurchase } from '../types';
import { supabaseAdmin } from '../lib/supabase';
import { useSortableData } from '../hooks/useSortableData';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
const VENDEDORES = ['Juan Pérez', 'María García', 'Carlos Ruiz', 'Ana López'];
const FARMACIAS = ['Farmacia Central', 'Farmacia El Sol', 'Farmacia La Salud', 'Farmacia Bienestar', 'Farmacia Nueva Vida'];
const INITIAL_PURCHASES: EmptyBoxPurchase[] = [{
  id: '1',
  farmacia: 'Farmacia Central',
  vendedor: 'Juan Pérez',
  fecha: new Date('2024-02-28'),
  cantidadCajas: 50,
  precioPorCaja: 25,
  total: 1250,
  estado: 'pagado'
}, {
  id: '2',
  farmacia: 'Farmacia El Sol',
  vendedor: 'María García',
  fecha: new Date('2024-02-27'),
  cantidadCajas: 30,
  precioPorCaja: 25,
  total: 750,
  estado: 'pendiente'
}, {
  id: '3',
  farmacia: 'Farmacia La Salud',
  vendedor: 'Carlos Ruiz',
  fecha: new Date('2024-02-26'),
  cantidadCajas: 100,
  precioPorCaja: 22,
  total: 2200,
  estado: 'pagado'
}, {
  id: '4',
  farmacia: 'Farmacia Bienestar',
  vendedor: 'Juan Pérez',
  fecha: new Date('2024-02-25'),
  cantidadCajas: 45,
  precioPorCaja: 25,
  total: 1125,
  estado: 'pagado'
}, {
  id: '5',
  farmacia: 'Farmacia Nueva Vida',
  vendedor: 'Ana López',
  fecha: new Date('2024-02-24'),
  cantidadCajas: 20,
  precioPorCaja: 25,
  total: 500,
  estado: 'pendiente'
}];
type FormData = {
  farmacia: string;
  vendedor: string;
  fecha: string;
  cantidadCajas: string;
  precioPorCaja: string;
  estado: EmptyBoxPurchase['estado'];
  nota: string;
};
const emptyForm: FormData = {
  farmacia: FARMACIAS[0],
  vendedor: VENDEDORES[0],
  fecha: new Date().toISOString().split('T')[0],
  cantidadCajas: '',
  precioPorCaja: '',
  estado: 'pendiente',
  nota: ''
};
export function CajaVaciaPage() {
  const [purchases, setPurchases] = useState<EmptyBoxPurchase[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vendedorFilter, setVendedorFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState<EmptyBoxPurchase | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const filteredPurchases = purchases.filter(p => {
    const matchesSearch = p.farmacia.toLowerCase().includes(searchQuery.toLowerCase()) || p.vendedor.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || p.estado === statusFilter;
    const matchesVendedor = vendedorFilter === 'all' || p.vendedor === vendedorFilter;
    return matchesSearch && matchesStatus && matchesVendedor;
  });
  // Sorting setup
  const {
    items: sortedPurchases,
    requestSort,
    sortConfig
  } = useSortableData(filteredPurchases, {
    farmacia: p => p.farmacia,
    vendedor: p => p.vendedor,
    fecha: p => new Date(p.fecha).getTime(),
    cantidadCajas: p => p.cantidadCajas,
    precioPorCaja: p => p.precioPorCaja,
    total: p => p.total,
    estado: p => p.estado
  });
  // Default sort by date descending if no sort config
  const finalPurchases = sortConfig ? sortedPurchases : [...sortedPurchases].sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  const totalCajas = finalPurchases.reduce((sum, p) => sum + p.cantidadCajas, 0);
  const inversionTotal = finalPurchases.reduce((sum, p) => sum + p.total, 0);
  const comprasPendientes = finalPurchases.filter(p => p.estado === 'pendiente').length;
  const computedTotal = (Number(formData.cantidadCajas) || 0) * (Number(formData.precioPorCaja) || 0);
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 0
  }).format(amount);
  const formatDate = (date: Date) => new Date(date).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
  const openAddModal = () => {
    setEditingPurchase(null);
    setFormData(emptyForm);
    setIsModalOpen(true);
  };
  const openEditModal = (purchase: EmptyBoxPurchase) => {
    setEditingPurchase(purchase);
    setFormData({
      farmacia: purchase.farmacia,
      vendedor: purchase.vendedor,
      fecha: new Date(purchase.fecha).toISOString().split('T')[0],
      cantidadCajas: String(purchase.cantidadCajas),
      precioPorCaja: String(purchase.precioPorCaja),
      estado: purchase.estado,
      nota: purchase.nota || ''
    });
    setIsModalOpen(true);
  };
  useEffect(() => {
    loadPurchases();
  }, []);
  const mapPurchase = (row: any): EmptyBoxPurchase => ({
    id: row.id,
    farmacia: row.farmacia,
    vendedor: row.vendedor,
    fecha: new Date(row.fecha),
    cantidadCajas: row.cantidad_cajas,
    precioPorCaja: row.precio_por_caja,
    total: row.total,
    estado: row.estado,
    nota: row.nota || undefined
  });
  const loadPurchases = async () => {
    setIsLoading(true);
    try {
      const {
        data
      } = await supabaseAdmin.from('empty_box_purchases').select('*').order('fecha', {
        ascending: false
      });
      if (data) setPurchases(data.map(mapPurchase));
    } finally {
      setIsLoading(false);
    }
  };
  const handleSave = async () => {
    if (!formData.cantidadCajas || !formData.precioPorCaja) return;
    setIsSaving(true);
    try {
      const payload = {
        farmacia: formData.farmacia,
        vendedor: formData.vendedor,
        fecha: new Date(formData.fecha).toISOString(),
        cantidad_cajas: Number(formData.cantidadCajas),
        precio_por_caja: Number(formData.precioPorCaja),
        total: computedTotal,
        estado: formData.estado,
        nota: formData.nota || null
      };
      if (editingPurchase) {
        const {
          data
        } = await supabaseAdmin.from('empty_box_purchases').update(payload).eq('id', editingPurchase.id).select().single();
        if (data) setPurchases(prev => prev.map(p => p.id === editingPurchase.id ? mapPurchase(data) : p));
      } else {
        const {
          data
        } = await supabaseAdmin.from('empty_box_purchases').insert(payload).select().single();
        if (data) setPurchases(prev => [mapPurchase(data), ...prev]);
      }
      setIsModalOpen(false);
    } finally {
      setIsSaving(false);
    }
  };
  const handleDelete = async () => {
    if (!editingPurchase) return;
    await supabaseAdmin.from('empty_box_purchases').delete().eq('id', editingPurchase.id);
    setPurchases(prev => prev.filter(p => p.id !== editingPurchase.id));
    setIsDeleteModalOpen(false);
    setIsModalOpen(false);
  };
  return <div className="space-y-6" data-id="element-375">
      <PageHeader title="Compra de Caja Vacía" subtitle="Registro de compra de cajas a farmacias" icon={<BoxIcon className="w-6 h-6" data-id="element-377" />} data-id="element-376">
        <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-379" />} onClick={openAddModal} data-id="element-378">
          Registrar Compra
        </Button>
      </PageHeader>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 animate-stagger-1" data-id="element-380">
        <StatsCard title="Total Cajas" value={totalCajas} icon={<BoxIcon className="w-5 h-5" data-id="element-382" />} variant="primary" data-id="element-381" />
        <StatsCard title="Inversión Total" value={formatCurrency(inversionTotal)} icon={<DollarSignIcon className="w-5 h-5" data-id="element-384" />} variant="success" data-id="element-383" />
        <StatsCard title="Pagos Pendientes" value={comprasPendientes} icon={<ClockIcon className="w-5 h-5" data-id="element-386" />} variant="warning" data-id="element-385" />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 animate-stagger-2" data-id="element-387">
        <div className="relative flex-1" data-id="element-388">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-389" />
          <input type="text" placeholder="Buscar por farmacia o vendedor..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4]" data-id="element-390" />
        </div>
        <select value={vendedorFilter} onChange={e => setVendedorFilter(e.target.value)} className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-391">
          <option value="all" data-id="element-392">Todos los vendedores</option>
          {VENDEDORES.map(v => <option key={v} value={v} data-id="element-393">
              {v}
            </option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-394">
          <option value="all" data-id="element-395">Todos los estados</option>
          <option value="pendiente" data-id="element-396">Pendiente</option>
          <option value="pagado" data-id="element-397">Pagado</option>
        </select>
      </div>

      {/* Table */}
      <Card padding="none" className="animate-stagger-3" data-id="element-398">
        {filteredPurchases.length === 0 ? <div className="p-16 text-center animate-fade-in" data-id="element-399">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-400">
              <BoxIcon className="w-8 h-8 text-slate-400" data-id="element-401" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-402">
              No hay registros
            </h3>
            <p className="text-slate-500 text-sm" data-id="element-403">
              No se encontraron compras con los filtros aplicados
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-404">
            <table className="w-full" data-id="element-405">
              <thead data-id="element-406">
                <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-407">
                  <SortableTableHeader label="Farmacia" sortKey="farmacia" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-408" />
                  <SortableTableHeader label="Vendedor" sortKey="vendedor" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-409" />
                  <SortableTableHeader label="Fecha" sortKey="fecha" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-410" />
                  <SortableTableHeader label="Cajas" sortKey="cantidadCajas" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-411" />
                  <SortableTableHeader label="Precio/Caja" sortKey="precioPorCaja" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-412" />
                  <SortableTableHeader label="Total" sortKey="total" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-413" />
                  <SortableTableHeader label="Estado" sortKey="estado" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-414" />
                  <th className="px-5 py-4 w-20" data-id="element-415"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-416">
                {finalPurchases.map(purchase => <tr key={purchase.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-417">
                    <td className="px-5 py-4 font-medium text-slate-900" data-id="element-418">
                      {purchase.farmacia}
                    </td>
                    <td className="px-5 py-4" data-id="element-419">
                      <div className="flex items-center gap-1.5 text-sm text-slate-600" data-id="element-420">
                        <UserIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-421" />
                        {purchase.vendedor}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-600" data-id="element-422">
                      {formatDate(purchase.fecha)}
                    </td>
                    <td className="px-5 py-4 text-center font-medium text-slate-900" data-id="element-423">
                      {purchase.cantidadCajas}
                    </td>
                    <td className="px-5 py-4 text-right text-sm text-slate-600" data-id="element-424">
                      {formatCurrency(purchase.precioPorCaja)}
                    </td>
                    <td className="px-5 py-4 text-right font-bold text-slate-900" data-id="element-425">
                      {formatCurrency(purchase.total)}
                    </td>
                    <td className="px-5 py-4 text-center" data-id="element-426">
                      {purchase.estado === 'pagado' ? <Badge variant="success" data-id="element-427">Pagado</Badge> : <Badge variant="warning" data-id="element-428">Pendiente</Badge>}
                    </td>
                    <td className="px-5 py-4" data-id="element-429">
                      <div className="flex items-center gap-1" data-id="element-430">
                        <button onClick={() => openEditModal(purchase)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-431">
                          <EditIcon className="w-4 h-4" data-id="element-432" />
                        </button>
                        <button onClick={() => {
                    setEditingPurchase(purchase);
                    setIsDeleteModalOpen(true);
                  }} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-433">
                          <TrashIcon className="w-4 h-4" data-id="element-434" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </Card>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingPurchase ? 'Editar Compra' : 'Registrar Compra de Caja Vacía'} size="md" data-id="element-435">
        <ModalBody data-id="element-436">
          <div className="space-y-4" data-id="element-437">
            <div className="grid grid-cols-2 gap-4" data-id="element-438">
              <div data-id="element-439">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-440">
                  Farmacia
                </label>
                <select value={formData.farmacia} onChange={e => setFormData({
                ...formData,
                farmacia: e.target.value
              })} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-441">
                  {FARMACIAS.map(f => <option key={f} value={f} data-id="element-442">
                      {f}
                    </option>)}
                </select>
              </div>
              <div data-id="element-443">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-444">
                  Vendedor
                </label>
                <select value={formData.vendedor} onChange={e => setFormData({
                ...formData,
                vendedor: e.target.value
              })} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-445">
                  {VENDEDORES.map(v => <option key={v} value={v} data-id="element-446">
                      {v}
                    </option>)}
                </select>
              </div>
            </div>
            <Input label="Fecha" type="date" value={formData.fecha} onChange={e => setFormData({
            ...formData,
            fecha: e.target.value
          })} data-id="element-447" />
            <div className="grid grid-cols-2 gap-4" data-id="element-448">
              <Input label="Cantidad de Cajas" type="number" value={formData.cantidadCajas} onChange={e => setFormData({
              ...formData,
              cantidadCajas: e.target.value
            })} placeholder="0" data-id="element-449" />
              <Input label="Precio por Caja (DOP)" type="number" value={formData.precioPorCaja} onChange={e => setFormData({
              ...formData,
              precioPorCaja: e.target.value
            })} placeholder="0" data-id="element-450" />
            </div>
            {/* Auto-calculated total */}
            {computedTotal > 0 && <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200" data-id="element-451">
                <span className="text-sm font-medium text-slate-600" data-id="element-452">
                  Total calculado:
                </span>
                <span className="text-lg font-bold text-slate-900" data-id="element-453">
                  {formatCurrency(computedTotal)}
                </span>
              </div>}
            <div data-id="element-454">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-455">
                Estado
              </label>
              <select value={formData.estado} onChange={e => setFormData({
              ...formData,
              estado: e.target.value as EmptyBoxPurchase['estado']
            })} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-456">
                <option value="pendiente" data-id="element-457">Pendiente</option>
                <option value="pagado" data-id="element-458">Pagado</option>
              </select>
            </div>
            <div data-id="element-459">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-460">
                Nota (opcional)
              </label>
              <input type="text" value={formData.nota} onChange={e => setFormData({
              ...formData,
              nota: e.target.value
            })} placeholder="Observaciones..." className="block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-461" />
            </div>
          </div>
        </ModalBody>
        <ModalFooter data-id="element-462">
          <Button variant="secondary" onClick={() => setIsModalOpen(false)} data-id="element-463">
            Cancelar
          </Button>
          <Button onClick={handleSave} isLoading={isSaving} disabled={!formData.cantidadCajas || !formData.precioPorCaja} data-id="element-464">
            {editingPurchase ? 'Guardar Cambios' : 'Registrar Compra'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirm */}
      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Eliminar Registro" size="sm" data-id="element-465">
        <ModalBody data-id="element-466">
          <p className="text-slate-600" data-id="element-467">
            ¿Eliminar la compra de <strong data-id="element-468">{editingPurchase?.farmacia}</strong>?
            Esta acción no se puede deshacer.
          </p>
        </ModalBody>
        <ModalFooter data-id="element-469">
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)} data-id="element-470">
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDelete} data-id="element-471">
            Eliminar
          </Button>
        </ModalFooter>
      </Modal>
    </div>;
}